import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { useRBAC, Role } from '../hooks/useRBAC';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUserStore } from '../stores/useUserStore';
import { Button } from './ui/button';
import { cn } from '../lib/utils';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from './ui/sheet';

// Lucide React icons
import {
  Home,
  ShoppingCart,
  DollarSign,
  FolderTree,
  Settings,
  Coffee,
  Users,
  BarChart3,
  Package,
  TrendingUp,
  ChefHat,
  FlaskConical,
  Warehouse,
  Receipt,
  ChevronDown,
  ChevronRight,
  Link,
  RefreshCw,
  Calendar,
  MoreVertical,
  Star,
  FileText,
  Clock,
  Kanban,
  GanttChart,
} from 'lucide-react';

// Navigation configuration with groups
interface NavigationItem {
  id: string;
  labelKey: string;
  icon: React.ComponentType<{ className?: string }>;
  path: string;
}

interface NavigationGroup {
  id: string;
  labelKey: string;
  icon: React.ComponentType<{ className?: string }>;
  items: NavigationItem[];
}

export const navigationGroups: NavigationGroup[] = [
  {
    id: 'sales_orders',
    labelKey: 'pos.navigation_groups.sales_orders',
    icon: Receipt,
    items: [
      {
        id: 'orders',
        labelKey: 'pos.orders.name',
        icon: ShoppingCart,
        path: '/orders',
      },
      {
        id: 'orders_report',
        labelKey: 'pos.reports_orders.name',
        icon: BarChart3,
        path: '/report/orders',
      },
      {
        id: 'customers',
        labelKey: 'pos.customers.name',
        icon: Users,
        path: '/customers',
      },
      {
        id: 'customer_reviews',
        labelKey: 'pos.customer_reviews.name',
        icon: Star,
        path: '/customer_reviews',
      },
    ],
  },
  {
    id: 'marketing',
    labelKey: 'pos.navigation_groups.marketing',
    icon: TrendingUp,
    items: [
      {
        id: 'campaigns',
        labelKey: 'pos.campaigns.title',
        icon: TrendingUp,
        path: '/campaigns',
      },
    ],
  },
  {
    id: 'products',
    labelKey: 'pos.navigation_groups.products',
    icon: Package,
    items: [
      {
        id: 'products',
        labelKey: 'pos.products.name',
        icon: Package,
        path: '/products',
      },
      {
        id: 'product_categories',
        labelKey: 'pos.product_categories.title',
        icon: FolderTree,
        path: '/product_categories',
      },
      {
        id: 'menus',
        labelKey: 'pos.menus.title',
        icon: Coffee,
        path: '/menus',
      },
    ],
  },
  {
    id: 'recipes_ingredients',
    labelKey: 'pos.navigation_groups.recipes_ingredients',
    icon: ChefHat,
    items: [
      {
        id: 'recipes',
        labelKey: 'pos.recipes.name',
        icon: ChefHat,
        path: '/recipes',
      },
      {
        id: 'ingredients',
        labelKey: 'pos.ingredients.name',
        icon: FlaskConical,
        path: '/ingredients',
      },
    ],
  },
  {
    id: 'inventory',
    labelKey: 'pos.navigation_groups.inventory',
    icon: Warehouse,
    items: [
      {
        id: 'inventory',
        labelKey: 'pos.inventory.name',
        icon: Package,
        path: '/inventory',
      },
      {
        id: 'warehouses',
        labelKey: 'pos.warehouses.name',
        icon: Warehouse,
        path: '/warehouses',
      },
      {
        id: 'inventory_categories',
        labelKey: 'pos.inventoryCategories.name',
        icon: FolderTree,
        path: '/inventory_categories',
      },
    ],
  },
  {
    id: 'costs',
    labelKey: 'pos.navigation_groups.costs',
    icon: DollarSign,
    items: [
      {
        id: 'costs',
        labelKey: 'pos.costs.title',
        icon: DollarSign,
        path: '/costs',
      },
      {
        id: 'cost_categories',
        labelKey: 'pos.cost_categories.title',
        icon: FolderTree,
        path: '/cost_categories',
      },
    ],
  },
  {
    id: 'staff',
    labelKey: 'pos.navigation_groups.staff',
    icon: Users,
    items: [
      {
        id: 'staff_profiles',
        labelKey: 'pos.staff.header.title',
        icon: Users,
        path: '/staff_profiles',
      },
      {
        id: 'staff_management',
        labelKey: 'pos.menu.staff',
        icon: Users,
        path: '/staff_management',
      },
      {
        id: 'shift_planning',
        labelKey: 'pos.staff.tabs.shift_planning',
        icon: Calendar,
        path: '/shift_planning',
      },
      {
        id: 'attendance_management',
        labelKey: 'pos.staff.tabs.attendance_management',
        icon: Clock,
        path: '/attendance_management',
      },
      {
        id: 'bonuses',
        labelKey: 'pos.bonuses.title',
        icon: DollarSign,
        path: '/bonuses',
      },
      {
        id: 'salary_forecast',
        labelKey: 'pos.salary_forecast.title',
        icon: DollarSign,
        path: '/report/salary-forecast',
      },
      {
        id: 'policies',
        labelKey: 'pos.policies.title',
        icon: FileText,
        path: '/policies',
      },
    ],
  },
  {
    id: 'project_management',
    labelKey: 'pos.navigation_groups.project_management',
    icon: Kanban,
    items: [
      {
        id: 'kanban_board',
        labelKey: 'pos.project_management.kanban_board',
        icon: Kanban,
        path: '/project-management/kanban',
      },
      {
        id: 'gantt_chart',
        labelKey: 'pos.project_management.gantt_chart',
        icon: GanttChart,
        path: '/project-management/gantt',
      },
    ],
  },
  {
    id: 'settings',
    labelKey: 'pos.navigation_groups.settings',
    icon: Settings,
    items: [
      {
        id: 'settings',
        labelKey: 'pos.settings.title',
        icon: Settings,
        path: '/settings',
      },
      {
        id: 'grab_mappings',
        labelKey: 'pos.grab.mappings.title',
        icon: Link,
        path: '/grab/mappings',
      },
      {
        id: 'grab_settings',
        labelKey: 'pos.grab.settings.title',
        icon: Settings,
        path: '/grab/settings',
      },
      {
        id: 'grab_sync',
        labelKey: 'pos.grab.sync.title',
        icon: RefreshCw,
        path: '/grab/sync',
      },
    ],
  },
];

export const ModernNavigation = ({
  isCollapsed = false,
}: {
  isCollapsed?: boolean;
}) => {
  const { translate } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, fetchUser } = useUserStore();
  const {
    canAccessResource,
    hasPermission: hasRBACPermission,
    userRoles,
    highestRole,
  } = useRBAC();
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  useEffect(() => {
    // Ensure user data is loaded
    if (!user) {
      fetchUser();
    }
  }, [user, fetchUser]);

  const hasPermission = (resource: string) => {
    // If user is not loaded yet, show basic navigation items
    if (!user) {
      const basicResources = [
        'dashboard',
        'orders',
        'orders_report',
        'customers',
        'campaigns',
        'products',
        'product_categories',
        'recipes',
        'ingredients',
        'inventory',
        'warehouses',
        'inventory_categories',
        'costs',
        'cost_categories',
        'settings',
        'grab_mappings',
        'grab_settings',
        'grab_sync',
      ];
      return basicResources.includes(resource);
    }

    // For recipes, ingredients, and campaigns, always use RBAC check (don't bypass for sysAdmin)
    if (
      resource === 'recipes' ||
      resource === 'ingredients' ||
      resource === 'campaigns'
    ) {
      const rbacResource = resource;
      return canAccessResource(rbacResource as any);
    }

    // sysAdmin gets access to everything EXCEPT recipes, ingredients, and campaigns (which are checked above)
    // Use userRoles from useRBAC to respect role simulation
    if (userRoles?.includes('sysAdmin')) return true;

    // If user has no roles or empty roles, show basic navigation
    // Use highestRole from useRBAC to respect role hierarchy and simulation
    if (!highestRole) {
      const basicResources = [
        'dashboard',
        'orders',
        'orders_report',
        'customers',
        'campaigns',
        'products',
        'product_categories',
        'recipes',
        'ingredients',
        'inventory',
        'warehouses',
        'inventory_categories',
        'costs',
        'cost_categories',
        'settings',
        'grab_mappings',
        'grab_settings',
        'grab_sync',
      ];
      return basicResources.includes(resource);
    }

    // Check admin first (highest level) - includes orgAdmin
    // Use highestRole from useRBAC to respect role hierarchy and simulation
    if (
      highestRole === 'admin' ||
      highestRole === 'orgAdmin' ||
      highestRole === 'sysAdmin'
    )
      return true;

    const isStoreManager = highestRole === 'storeManager';
    const isStaff = highestRole === 'staff';
    const isCashier = highestRole === 'cashier';

    // Map navigation resource IDs to RBAC resource names
    const resourceMap: Record<string, string> = {
      dashboard: 'dashboard',
      orders: 'orders',
      orders_report: 'orders_report',
      campaigns: 'campaigns',
      products: 'products',
      product_categories: 'product_categories',
      recipes: 'recipes',
      ingredients: 'ingredients',
      inventory: 'inventory',
      warehouses: 'warehouses',
      costs: 'costs',
      cost_categories: 'cost_categories',
      staff_profiles: 'staff_profiles',
      staff_management: 'staff_profiles', // Use staff_profiles permission for staff management
      bonuses: 'bonuses', // Use bonuses resource
      salary_forecast: 'salary_forecast', // Use dedicated salary_forecast resource
      policies: 'staff_profiles', // Use staff_profiles permission for policies
      settings: 'settings',
      customers: 'customers',
      customer_reviews: 'customer_reviews',
      books: 'books',
      book_categories: 'book_categories',
      book_rents: 'book_rents',
      menus: 'menus',
      grab_mappings: 'products', // Use products permission for mappings
      grab_settings: 'settings', // Use settings permission for integration settings
      grab_sync: 'products', // Use products permission for sync
      kanban_board: 'project_management',
      gantt_chart: 'project_management',
    };

    // Special case for staff_management: requires edit permission OR storeManager role
    // Staff role should NOT have access to staff management (only menuStaff menu access)
    // Use highestRole to respect role hierarchy
    if (resource === 'staff_management') {
      if (!highestRole) return hasRBACPermission('staff_profiles', 'edit');

      // StoreManager and above should have access even without edit permission
      const allowedRoles: Role[] = [
        'storeManager',
        'admin',
        'orgAdmin',
        'sysAdmin',
      ];
      if (allowedRoles.includes(highestRole)) {
        return true;
      }
      // For other roles, check edit permission
      return hasRBACPermission('staff_profiles', 'edit');
    }

    // Special case for inventory: only for Store Manager and Admin (NOT staff)
    // Staff can scan QR codes from dashboard but not see inventory management menu
    // Use highest role to determine access (respects role hierarchy)
    if (resource === 'inventory') {
      if (!highestRole) return false;

      // Explicitly exclude staff role (even if it's the highest)
      if (highestRole === 'staff') {
        return false;
      }

      // StoreManager and above should have access
      const allowedRoles: Role[] = [
        'storeManager',
        'admin',
        'orgAdmin',
        'sysAdmin',
      ];
      return allowedRoles.includes(highestRole);
    }

    // Special case for menus: only for Store Manager and Admin (NOT staff)
    // Staff can see menu items (menuSales, menuZalo, etc.) but not menus management
    // Use highest role to determine access (respects role hierarchy)
    if (resource === 'menus') {
      if (!highestRole) return false;

      // Explicitly exclude staff role (even if it's the highest)
      if (highestRole === 'staff') {
        return false;
      }

      // StoreManager and above should have access
      const allowedRoles: Role[] = [
        'storeManager',
        'admin',
        'orgAdmin',
        'sysAdmin',
      ];
      return allowedRoles.includes(highestRole);
    }

    // Special case for customers: only for Store Manager and Admin (NOT staff)
    // Staff cannot access customers management page
    // Use highest role to determine access (respects role hierarchy)
    if (resource === 'customers') {
      if (!highestRole) return false;

      // Explicitly exclude staff role (even if it's the highest)
      if (highestRole === 'staff') {
        return false;
      }

      // StoreManager and above should have access
      const allowedRoles: Role[] = [
        'storeManager',
        'admin',
        'orgAdmin',
        'sysAdmin',
      ];
      return allowedRoles.includes(highestRole);
    }

    // Special case for costs: only for Store Manager and Admin (NOT staff)
    // Staff can create costs from dashboard quick actions but not access costs management page
    // Use highest role to determine access (respects role hierarchy)
    if (resource === 'costs') {
      if (!highestRole) return false;

      // Explicitly exclude staff role (even if it's the highest)
      if (highestRole === 'staff') {
        return false;
      }

      // StoreManager and above should have access
      const allowedRoles: Role[] = [
        'storeManager',
        'admin',
        'orgAdmin',
        'sysAdmin',
      ];
      return allowedRoles.includes(highestRole);
    }

    // Special case for cost_categories: only for Store Manager and Admin (NOT staff)
    // Staff cannot access cost categories management page
    // Use highest role to determine access (respects role hierarchy)
    if (resource === 'cost_categories') {
      if (!highestRole) return false;

      // Explicitly exclude staff role (even if it's the highest)
      if (highestRole === 'staff') {
        return false;
      }

      // StoreManager and above should have access
      const allowedRoles: Role[] = [
        'storeManager',
        'admin',
        'orgAdmin',
        'sysAdmin',
      ];
      return allowedRoles.includes(highestRole);
    }

    // Special case for policies: only for Store Manager and Admin (NOT staff)
    // Staff can see policies in "My Neko" but not the Policy Management menu
    // Use highest role to determine access (respects role hierarchy)
    if (resource === 'policies') {
      if (!highestRole) return false;

      // Explicitly exclude staff role (even if it's the highest)
      if (highestRole === 'staff') {
        return false;
      }

      // StoreManager and above should have access
      const allowedRoles: Role[] = [
        'storeManager',
        'admin',
        'orgAdmin',
        'sysAdmin',
      ];
      return allowedRoles.includes(highestRole);
    }

    // Special case for salary_forecast: only for Store Manager and Admin (NOT staff)
    // Use highest role to determine access (respects role hierarchy)
    if (resource === 'salary_forecast') {
      if (!highestRole) return false;

      // Explicitly exclude staff role (even if it's the highest)
      if (highestRole === 'staff') {
        return false;
      }

      // StoreManager and above should have access
      const allowedRoles: Role[] = [
        'storeManager',
        'admin',
        'orgAdmin',
        'sysAdmin',
      ];
      return allowedRoles.includes(highestRole);
    }

    // Special case for shift_planning: only for Store Manager and Admin (NOT staff)
    // Use highest role to determine access (respects role hierarchy)
    if (resource === 'shift_planning') {
      if (!highestRole) return false;

      // Explicitly exclude staff role (even if it's the highest)
      if (highestRole === 'staff') {
        return false;
      }

      // Check if highest role is one of the allowed roles
      const allowedRoles: Role[] = [
        'storeManager',
        'admin',
        'orgAdmin',
        'sysAdmin',
      ];
      return allowedRoles.includes(highestRole);
    }

    // Special case for attendance_management: only for Store Manager and Admin (NOT staff)
    if (resource === 'attendance_management') {
      if (!highestRole) return false;

      // Explicitly exclude staff role
      if (highestRole === 'staff') {
        return false;
      }

      const allowedRoles: Role[] = [
        'storeManager',
        'admin',
        'orgAdmin',
        'sysAdmin',
      ];
      return allowedRoles.includes(highestRole);
    }

    // Special case for bonuses: only for Store Manager and Admin (NOT staff)
    // Use highest role to determine access (respects role hierarchy)
    if (resource === 'bonuses') {
      if (!highestRole) return false;

      // Explicitly exclude staff role (even if it's the highest)
      if (highestRole === 'staff') {
        return false;
      }

      // StoreManager and above should have access
      const allowedRoles: Role[] = [
        'storeManager',
        'admin',
        'orgAdmin',
        'sysAdmin',
      ];
      return allowedRoles.includes(highestRole);
    }

    const rbacResource = resourceMap[resource];
    if (rbacResource) {
      // Use RBAC to check access for mapped resources
      return canAccessResource(rbacResource as any);
    }

    // For menu items and other non-RBAC resources, use role-based logic
    if (resource.startsWith('menu')) {
      if (isStoreManager || isStaff || isCashier) return true;
    }

    // For Grab integration resources, allow store managers and admins
    // Use userRoles from useRBAC to respect role simulation
    if (resource.startsWith('grab_')) {
      if (
        isStoreManager ||
        userRoles?.includes('admin') ||
        userRoles?.includes('orgAdmin')
      )
        return true;
    }

    // Store managers get additional non-RBAC resources
    if (isStoreManager) {
      const managerResources = [
        'menuOrganization',
        'organizations',
        'organization_departments',
        'department_positions',
        'menuStaff',
        'menuSales',
        'menuZalo',
        'zalo/checkin',
        'menuLibrary',
        'menuCustomers',
        'reviews',
        'menuFinancial',
      ];
      return managerResources.includes(resource);
    }

    // Staff menu items
    if (isStaff) {
      const staffResources = [
        'menuSales',
        'menuZalo',
        'zalo/checkin',
        'menuLibrary',
        'menuStaff',
        'menuFinancial',
      ];
      return staffResources.includes(resource);
    }

    // Cashier menu items
    if (isCashier) {
      const cashierResources = ['menuSales', 'menuFinancial'];
      return cashierResources.includes(resource);
    }

    return false;
  };

  // Auto-expand groups that contain the current active item
  useEffect(() => {
    const activeGroup = navigationGroups.find((group) =>
      group.items.some((item) => {
        const active =
          location.pathname === item.path ||
          (item.path !== '/dashboard' &&
            location.pathname.startsWith(item.path));
        return active && hasPermission(item.id);
      })
    );

    if (activeGroup) {
      setExpandedGroups((prev) => new Set(prev).add(activeGroup.id));
    }
  }, [location.pathname]);

  const toggleGroup = (groupId: string) => {
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };

  const isActive = (path: string) => {
    if (path === 'dashboard') {
      return (
        location.pathname === '/dashboard' ||
        location.pathname.includes('/dashboard')
      );
    }
    return location.pathname.includes(`/${path}`);
  };

  return (
    <nav className="w-full">
      {/* Dashboard */}
      <div className="px-2 py-1">
        <Button
          variant={isActive('dashboard') ? 'default' : 'ghost'}
          className={cn(
            'w-full justify-start',
            isCollapsed && 'justify-center px-2'
          )}
          onClick={() => navigate('/dashboard')}
        >
          <Home className={cn('h-4 w-4', !isCollapsed && 'mr-2')} />
          {!isCollapsed && <span>{translate('pos.dashboard.name')}</span>}
        </Button>
      </div>

      {/* Navigation Groups */}
      {navigationGroups.map((group) => {
        // Filter items that user has permission to access
        const visibleItems = group.items.filter((item) =>
          hasPermission(item.id)
        );

        // Skip the entire group if no items are visible
        if (visibleItems.length === 0) return null;

        const isExpanded = expandedGroups.has(group.id);

        // Check if any item in the group is active
        const hasActiveItem = visibleItems.some((item) => {
          const active =
            location.pathname === item.path ||
            (item.path !== '/dashboard' &&
              location.pathname.startsWith(item.path));
          return active;
        });

        return (
          <div key={group.id} className="px-2 py-1">
            {/* Group Header - Clickable to expand/collapse or navigate when collapsed */}
            {isCollapsed ? (
              <Button
                variant={hasActiveItem ? 'default' : 'ghost'}
                size="icon"
                className="w-full"
                onClick={() => {
                  // When collapsed, navigate to first item in group
                  if (visibleItems.length > 0) {
                    navigate(visibleItems[0].path);
                  }
                }}
                title={translate(group.labelKey)}
              >
                <group.icon className="h-4 w-4" />
              </Button>
            ) : (
              <Button
                variant="ghost"
                className="w-full justify-between font-medium"
                onClick={() => toggleGroup(group.id)}
              >
                <div className="flex items-center gap-2">
                  <group.icon className="h-4 w-4" />
                  <span>{translate(group.labelKey)}</span>
                </div>
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </Button>
            )}

            {/* Group Items - Collapsible */}
            {!isCollapsed && (
              <div
                className={cn(
                  'overflow-hidden transition-all duration-200 ease-in-out',
                  isExpanded ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                )}
              >
                <div className="pl-4 space-y-1">
                  {visibleItems.map((item) => {
                    // Check if current path matches or starts with the item path
                    const active =
                      location.pathname === item.path ||
                      (item.path !== '/dashboard' &&
                        location.pathname.startsWith(item.path));
                    const Icon = item.icon;

                    // Get translated label
                    const getLabel = () => {
                      const translated = translate(item.labelKey);
                      // Handle the |||| splitter in resource names (e.g., "Order |||| Orders")
                      return translated.includes('||||')
                        ? translated.split('||||')[0].trim()
                        : translated;
                    };

                    return (
                      <Button
                        key={item.id}
                        variant={active ? 'default' : 'ghost'}
                        className="w-full justify-start"
                        onClick={() => navigate(item.path)}
                      >
                        <Icon className="h-4 w-4 mr-2" />
                        <span>{getLabel()}</span>
                      </Button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
};

// Sidebar Navigation Component
export const ModernSidebar = ({
  isCollapsed = false,
  onToggle: _onToggle,
}: {
  isCollapsed?: boolean;
  onToggle?: () => void;
}) => {
  const { translate } = useTranslation();
  const location = useLocation();

  // Function to get the current page title based on the route
  const getCurrentPageTitle = () => {
    const path = location.pathname;

    // Helper function to handle React Admin's pluralization format
    const getSingularName = (translationKey: string) => {
      const translated = translate(translationKey);
      // Handle the |||| splitter in resource names (e.g., "Order |||| Orders")
      return translated.includes('||||')
        ? translated.split('||||')[0].trim()
        : translated;
    };

    // Dashboard
    if (path === '/') {
      return translate('pos.dashboard.name');
    }

    // Orders
    if (path.startsWith('/orders')) {
      return getSingularName('pos.orders.name');
    }

    // Orders Report
    if (path.startsWith('/report/orders')) {
      return translate('pos.reports_orders.name');
    }

    // Campaign Report
    if (path.startsWith('/report/campaigns')) {
      return translate('pos.campaign_report.title');
    }

    // Campaigns
    if (path.startsWith('/campaigns')) {
      return translate('pos.campaigns.title');
    }

    // Invoices
    if (path.startsWith('/invoices')) {
      return getSingularName('pos.invoices.name');
    }

    // Customers
    if (path.startsWith('/customers')) {
      return getSingularName('pos.customers.name');
    }

    // Products
    if (
      path.startsWith('/products') &&
      !path.startsWith('/product_categories')
    ) {
      return getSingularName('pos.products.name');
    }

    // Product Categories
    if (path.startsWith('/product_categories')) {
      return translate('pos.product_categories.title');
    }

    // Recipes
    if (path.startsWith('/recipes')) {
      return translate('pos.recipes.name', 'Recipes');
    }

    // Ingredients
    if (path.startsWith('/ingredients')) {
      return translate('pos.ingredients.name', 'Ingredients');
    }

    // Warehouses
    if (path.startsWith('/warehouses')) {
      return translate('pos.warehouses.name', 'Warehouses');
    }

    // My Neko / Staff Profiles
    if (path.startsWith('/staff_profiles')) {
      return translate('pos.staff.header.title');
    }

    // Staff Management
    if (path.startsWith('/staff_management')) {
      return translate('pos.menu.staff', 'Staff Management');
    }

    // Shift Planning
    if (path.startsWith('/shift_planning')) {
      return translate('pos.staff.tabs.shift_planning', 'Shift Planning');
    }

    // Bonuses
    if (path.startsWith('/bonuses')) {
      return translate('pos.bonuses.title', 'Bonuses');
    }

    // Costs
    if (path.startsWith('/costs')) {
      return getSingularName('pos.costs.name');
    }

    // Cost Categories
    if (path.startsWith('/cost_categories')) {
      return translate('pos.cost_categories.title');
    }

    // Policies
    if (path.startsWith('/policies')) {
      return translate('pos.policies.title');
    }

    // Settings
    if (path.startsWith('/settings')) {
      return translate('pos.settings.title');
    }

    // Default fallback
    return translate('pos.dashboard.name');
  };

  return (
    <div
      className={cn(
        'bg-background border-r transition-all duration-300 relative flex flex-col h-full',
        isCollapsed ? 'w-12' : 'w-64'
      )}
    >
      {/* Sidebar Header */}
      {!isCollapsed && (
        <div className="p-4 border-b flex items-center justify-center min-h-[64px]">
          <h2 className="text-lg font-semibold">{getCurrentPageTitle()}</h2>
        </div>
      )}

      {/* Navigation */}
      <div className="flex-1 overflow-auto py-2">
        <ModernNavigation isCollapsed={isCollapsed} />
      </div>

      {/* Sidebar Footer */}
      {!isCollapsed && (
        <div className="p-4 border-t">
          <p className="text-xs text-muted-foreground">
            {translate('pos.orders.modern.version')} v1.0.0
          </p>
        </div>
      )}
    </div>
  );
};

// Mobile Bottom Navigation
export const ModernMobileNavigation = () => {
  const { translate } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { highestRole, canAccessResource, hasPermission: hasRBACPermission } = useRBAC();
  const { user } = useUserStore();
  const [showMoreSheet, setShowMoreSheet] = useState(false);
  const [visibleItemsCount, setVisibleItemsCount] = useState(4);

  // Helper function to get singular form of resource names
  const getSingularLabel = useCallback(
    (translationKey: string) => {
      const translated = translate(translationKey);
      // Handle the |||| splitter in resource names (e.g., "Order |||| Orders")
      return translated.includes('||||')
        ? translated.split('||||')[0].trim()
        : translated;
    },
    [translate]
  );

  // Check if user has permission for a resource
  const hasPermission = useCallback(
    (resourceId: string) => {
      // If user is not loaded yet, allow basic resources
      if (!user) {
        const basicResources = [
          'dashboard',
          'orders',
          'orders_report',
          'customers',
          'campaigns',
          'products',
          'product_categories',
          'recipes',
          'ingredients',
          'inventory',
          'warehouses',
          'inventory_categories',
          'costs',
          'cost_categories',
          'settings',
          'grab_mappings',
          'grab_settings',
          'grab_sync',
        ];
        return basicResources.includes(resourceId);
      }

      // For recipes, ingredients, and campaigns, always use RBAC check
      if (
        resourceId === 'recipes' ||
        resourceId === 'ingredients' ||
        resourceId === 'campaigns'
      ) {
        return canAccessResource(resourceId as any);
      }

      // sysAdmin gets access to everything except those checked above
      if (highestRole === 'sysAdmin') return true;

      // If highest role is not determined yet, allow basic resources
      if (!highestRole) {
        const basicResources = [
          'dashboard',
          'orders',
          'orders_report',
          'customers',
          'campaigns',
          'products',
          'product_categories',
          'recipes',
          'ingredients',
          'inventory',
          'warehouses',
          'inventory_categories',
          'costs',
          'cost_categories',
          'settings',
          'grab_mappings',
          'grab_settings',
          'grab_sync',
        ];
        return basicResources.includes(resourceId);
      }

      const isStoreManager = highestRole === 'storeManager';
      const isStaff = highestRole === 'staff';
      const isCashier = highestRole === 'cashier';

      // Map navigation resource IDs to RBAC resource names
      const resourceMap: Record<string, string> = {
        dashboard: 'dashboard',
        orders: 'orders',
        orders_report: 'orders_report',
        campaigns: 'campaigns',
        products: 'products',
        product_categories: 'product_categories',
        recipes: 'recipes',
        ingredients: 'ingredients',
        inventory: 'inventory',
        warehouses: 'warehouses',
        costs: 'costs',
        cost_categories: 'cost_categories',
        staff_profiles: 'staff_profiles',
        staff_management: 'staff_profiles',
        bonuses: 'bonuses',
        salary_forecast: 'salary_forecast',
        policies: 'staff_profiles',
        settings: 'settings',
        customers: 'customers',
        customer_reviews: 'customer_reviews',
        books: 'books',
        book_categories: 'book_categories',
        book_rents: 'book_rents',
        menus: 'menus',
        grab_mappings: 'products',
        grab_settings: 'settings',
        grab_sync: 'products',
        kanban_board: 'project_management',
        gantt_chart: 'project_management',
      };

      // Special cases mirroring sidebar logic
      if (resourceId === 'inventory') {
        if (isStaff) return false;
        return ['storeManager', 'admin', 'orgAdmin', 'sysAdmin'].includes(
          highestRole
        );
      }

      // Staff Management requires edit permission or manager+
      if (resourceId === 'staff_management') {
        if (!highestRole) return false;
        const allowedRoles: Role[] = [
          'storeManager',
          'admin',
          'orgAdmin',
          'sysAdmin',
        ];
        if (allowedRoles.includes(highestRole)) {
          return true;
        }
        // For other roles, require edit permission on staff_profiles
        return hasRBACPermission('staff_profiles' as any, 'edit');
      }

      if (resourceId === 'menus') {
        if (isStaff) return false;
        return ['storeManager', 'admin', 'orgAdmin', 'sysAdmin'].includes(
          highestRole
        );
      }

      if (resourceId === 'customers') {
        if (isStaff) return false;
        return ['storeManager', 'admin', 'orgAdmin', 'sysAdmin'].includes(
          highestRole
        );
      }

      if (resourceId === 'costs') {
        if (isStaff) return false;
        return ['storeManager', 'admin', 'orgAdmin', 'sysAdmin'].includes(
          highestRole
        );
      }

      if (resourceId === 'cost_categories') {
        if (isStaff) return false;
        return ['storeManager', 'admin', 'orgAdmin', 'sysAdmin'].includes(
          highestRole
        );
      }

      if (resourceId === 'policies') {
        if (isStaff) return false;
        return ['storeManager', 'admin', 'orgAdmin', 'sysAdmin'].includes(
          highestRole
        );
      }

      if (resourceId === 'salary_forecast') {
        if (isStaff) return false;
        return ['storeManager', 'admin', 'orgAdmin', 'sysAdmin'].includes(
          highestRole
        );
      }

      if (resourceId === 'shift_planning') {
        if (isStaff) return false;
        return ['storeManager', 'admin', 'orgAdmin', 'sysAdmin'].includes(
          highestRole
        );
      }

      if (resourceId === 'attendance_management') {
        if (isStaff) return false;
        return ['storeManager', 'admin', 'orgAdmin', 'sysAdmin'].includes(
          highestRole
        );
      }

      if (resourceId === 'bonuses') {
        if (isStaff) return false;
        return ['storeManager', 'admin', 'orgAdmin', 'sysAdmin'].includes(
          highestRole
        );
      }

      const rbacResource = resourceMap[resourceId];
      if (rbacResource) {
        return canAccessResource(rbacResource as any);
      }

      // For menu items and other non-RBAC resources, use role-based logic
      if (resourceId.startsWith('menu')) {
        if (isStoreManager || isStaff || isCashier) return true;
      }

      if (resourceId.startsWith('grab_')) {
        if (isStoreManager || highestRole === 'admin' || highestRole === 'orgAdmin')
          return true;
      }

      return false;
    },
    [user, highestRole, canAccessResource]
  );

  // Define all possible mobile menu items
  const allMobileItems = useMemo(
    () => [
      {
        id: 'dashboard',
        label: translate('pos.dashboard.name'),
        icon: Home,
        path: '/dashboard',
        priority: 1, // Always show
      },
      {
        id: 'orders',
        label: getSingularLabel('pos.orders.name'),
        icon: Coffee,
        path: '/orders',
        priority: 2, // High priority
      },
      {
        id: 'staff_profiles',
        label: translate('pos.staff.header.title'),
        icon: Users,
        path: '/staff_profiles',
        priority: 3, // High priority for staff and store managers
      },
      {
        id: 'customers',
        label: getSingularLabel('pos.customers.name'),
        icon: Users,
        path: '/customers',
        priority: 4,
      },
      {
        id: 'products',
        label: getSingularLabel('pos.products.name'),
        icon: Package,
        path: '/products',
        priority: 5,
      },
      {
        id: 'costs',
        label: getSingularLabel('pos.costs.name'),
        icon: DollarSign,
        path: '/costs',
        priority: 6,
      },
      {
        id: 'settings',
        label: translate('pos.settings.title'),
        icon: Settings,
        path: '/settings',
        priority: 7,
      },
    ],
    [translate, getSingularLabel]
  );

  // Filter items based on permissions and role
  const sortedItems = useMemo(() => {
    const availableItems = allMobileItems.filter((item) => {
      return hasPermission(item.id);
    });

    // Sort by priority
    return [...availableItems].sort((a, b) => a.priority - b.priority);
  }, [allMobileItems, hasPermission]);

  // Calculate visible items based on screen width
  useEffect(() => {
    const calculateVisibleItems = () => {
      const width = window.innerWidth;
      // Estimate: each item needs ~80-90px width
      // Reserve space for "See More" button (if needed)
      let maxItems = Math.floor(width / 80);

      // Ensure at least 3 items are visible, max 5
      maxItems = Math.max(3, Math.min(maxItems, 5));

      // If we have more items than max, show one less to make room for "See More"
      if (sortedItems.length > maxItems) {
        maxItems = Math.max(3, maxItems - 1);
      }

      setVisibleItemsCount(maxItems);
    };

    calculateVisibleItems();
    window.addEventListener('resize', calculateVisibleItems);
    return () => window.removeEventListener('resize', calculateVisibleItems);
  }, [sortedItems.length]);

  const visibleItems = sortedItems.slice(0, visibleItemsCount);
  const hiddenItems = sortedItems.slice(visibleItemsCount);

  const isActive = (path: string) => {
    if (path === '/dashboard') {
      return (
        location.pathname === '/dashboard' ||
        location.pathname.includes('/dashboard')
      );
    }
    return location.pathname === path || location.pathname.startsWith(path);
  };

  const handleItemClick = (path: string) => {
    navigate(path);
    setShowMoreSheet(false);
  };

  // Build full accessible route list from navigationGroups for the More menu
  const accessibleItems = useMemo(() => {
    const items: {
      id: string;
      label: string;
      icon: React.ComponentType<{ className?: string }>;
      path: string;
    }[] = [];

    navigationGroups.forEach((group) => {
      group.items.forEach((item) => {
        if (hasPermission(item.id)) {
          items.push({
            id: item.id,
            label: getSingularLabel(item.labelKey),
            icon: item.icon,
            path: item.path,
          });
        }
      });
    });

    return items;
  }, [hasPermission, getSingularLabel, translate]);

  return (
    <>
      <div className="fixed bottom-0 left-0 right-0 z-50 border-t bg-background block lg:hidden">
        <div className="flex justify-around items-center h-16">
          {visibleItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <button
                key={item.id}
                onClick={() => handleItemClick(item.path)}
                className={cn(
                  'flex flex-col items-center justify-center flex-1 h-full space-y-1 transition-colors min-w-0',
                  active
                    ? 'text-primary'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                <Icon className="h-5 w-5 flex-shrink-0" />
                <span className="text-xs font-medium truncate w-full text-center px-1">
                  {item.label}
                </span>
              </button>
            );
          })}
          {accessibleItems.length > visibleItems.length && (
            <Sheet open={showMoreSheet} onOpenChange={setShowMoreSheet}>
              <SheetTrigger asChild>
                <button
                  className={cn(
                    'flex flex-col items-center justify-center flex-1 h-full space-y-1 transition-colors min-w-0',
                    'text-muted-foreground hover:text-foreground'
                  )}
                >
                  <MoreVertical className="h-5 w-5 flex-shrink-0" />
                  <span className="text-xs font-medium truncate w-full text-center px-1">
                    {translate('pos.dashboard.more', 'More')}
                  </span>
                </button>
              </SheetTrigger>
              <SheetContent side="bottom" className="flex flex-col max-h-[80vh] p-0">
                <SheetHeader className="px-6 pt-6 pb-4 flex-shrink-0">
                  <SheetTitle>
                    {translate('pos.dashboard.more_menu', 'More Menu')}
                  </SheetTitle>
                </SheetHeader>
                <div className="grid grid-cols-2 gap-4 py-4 px-6 overflow-y-auto flex-1">
                  {accessibleItems.map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item.path);
                    return (
                      <button
                        key={item.id}
                        onClick={() => handleItemClick(item.path)}
                        className={cn(
                          'flex flex-col items-center justify-center p-4 space-y-2 rounded-lg transition-colors',
                          active
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted hover:bg-muted/80 text-foreground'
                        )}
                      >
                        <Icon className="h-6 w-6" />
                        <span className="text-sm font-medium text-center">
                          {item.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </SheetContent>
            </Sheet>
          )}
        </div>
      </div>
    </>
  );
};
